import { customerTokenKey, STORE_ID, SHOPIFY_API_VERSION } from "utils/constants";
import { isValidToken, getCustomerAccessToken } from "utils/oauth";
import navigate from "helpers/navigate";
import axios from "axios";
import {
	customerQuery, customerAddressesQuery, customerAddressCreateMutation,
	customerAddressDeleteMutation, customerAddressUpdateMutation, changeDefaultAddressMutation,
	ordersQuery, metafieldsSetMutation, customerUpdateMutation
} from "./queries";

class CustomerResource {
	constructor() {
		this.customerApiCall = null;
		this.client = axios.create({
			baseURL: `https://shopify.com/${STORE_ID}/account/customer/api/${SHOPIFY_API_VERSION}/graphql`,
			headers: { "Content-Type": "application/json" },
		});
		this.customerApiCall = async (query, variables = {}) => {
			const token = getCustomerAccessToken();
			if (!isValidToken(token)) return;

			return this.client.post("", { query, variables }, {
				headers: { Authorization: token?.access_token }
			})
				.then(response => {
					if (response.status === 200) return Object.values(response.data.data)[0];
					else return null;
				})
				.catch(error => {
					console.error({ error: error.response?.data });
					if (error.response?.status === 401) {
						window.localStorage.removeItem(customerTokenKey);
						navigate("/account/login");
					}
					return null;
				});
		};
	}

	async getProfile() {
		const customer = await this.customerApiCall(customerQuery);
		if (customer) {
			return customer;
		}
		return null;
	}

	getAddresses(cursor) {
		const afterCursorEl = cursor ? `after: "${cursor},"` : "";
		return this.customerApiCall(customerAddressesQuery(afterCursorEl));
	}

	updateAddress(addressId, address, defaultAddress) {
		return this.customerApiCall(customerAddressUpdateMutation, { addressId, address, defaultAddress });
	}

	createAddress(address, defaultAddress) {
		return this.customerApiCall(customerAddressCreateMutation, { address, defaultAddress });
	}

	deleteAddress(addressId) {
		return this.customerApiCall(customerAddressDeleteMutation, { addressId });
	}

	changeDefaultAddress(addressId) {
		return this.customerApiCall(changeDefaultAddressMutation, { addressId, defaultAddress: true });
	}

	update(data) {
		return this.customerApiCall(customerUpdateMutation, {
			input: data,
		});
	}

	async changeEmail(id, email) {
		try {
			const response = await axios.post("/.netlify/functions/update-customer-info", {
				id,
				email
			});

			if (response.data.success) {
				return { success: true, customer: response.data.customer };
			}

			return {
				success: false,
				userErrors: response.data.userErrors
			};
		} catch (error) {
			console.error("Email change error:", error);
			return {
				success: false,
				error: error.message || "Failed to change email"
			};
		}
	}

	async changePhone(id, phone) {
		try {
			const response = await axios.post("/.netlify/functions/update-customer-info", {
				id,
				phone
			});

			if (response.data.success) {
				return { success: true, customer: response.data.customer };
			}

			return {
				success: false,
				userErrors: response.data.userErrors
			};
		} catch (error) {
			console.error("Phone change error:", error);
			return {
				success: false,
				error: error.message || "Failed to change phone"
			};
		}
	}

	getOrders(cursor) {
		const afterCursorEl = cursor ? `after: "${cursor},"` : "";
		return this.customerApiCall(ordersQuery(afterCursorEl));
	}

	getOrdersFromAdmin = async (customerId, cursor) => {
		const afterCursorEl = cursor ? `after: "${cursor},"` : "";

		const response = await axios("/.netlify/functions/admin-api-orders", {
			method: "POST",
			data: {
				customerId,
				afterCursorEl,
			},
		});
		return response.data;
	};

	async updateMetafields(key, discountCode, id, userMetafields) {
		const metafield = userMetafields.find(mf => mf?.key === key);
		const existingDiscountCodes = metafield ? JSON.parse(metafield.value) : [];

		if (!existingDiscountCodes.includes(discountCode)) {
			existingDiscountCodes.push(discountCode);
		}

		const metafields = [{ "key": key, "namespace": "custom", "ownerId": id, "value": JSON.stringify(existingDiscountCodes) }];

		const response = await this.customerApiCall(metafieldsSetMutation, { metafields });

		return response;
	}

	async updateClientSubStatus(id, status) {
		const metafields = [{
			key: "client_sub_status",
			namespace: "custom",
			ownerId: id,
			value: status || ""
		}];

		const response = await this.customerApiCall(metafieldsSetMutation, { metafields });
		return response;
	}

	async clearEversubsDiscounts(id) {
		const metafields = [{
			key: "discounts",
			namespace: "eversubs",
			ownerId: id,
			value: ""
		}];

		const response = await this.customerApiCall(metafieldsSetMutation, { metafields });
		return response;
	}

	async setChurnDiscountMetafield(id, key) {
		const metafields = [{
			key,
			namespace: "churn",
			ownerId: id,
			type: "boolean",
			value: "true"
		}];

		const response = await this.customerApiCall(metafieldsSetMutation, { metafields });
		return response;
	}
}

export default CustomerResource;